// src/app/profile/components/ProfileDetails.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit2, MapPin, Mail, Phone, Globe, Heart, Image as ImageIcon, Video, BadgeCheck, Clock, ShieldAlert, ShieldQuestion, ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import Spinner from '@/components/ui/spinner';
import InlineBanner from '@/components/ui/inline-banner';
// Removed PostCard, ImageGalleryCard, and VideoGalleryCard from profile feed for now
import { toAbsoluteMediaUrl, toAvatarUrl, maybeCacheBust } from '@/lib/media';
import { useToast } from '@/components/ui/toast';
import { track } from '@/lib/analytics';
import { apiGet, apiPost } from '@/lib/api';
import { normalizeProfile } from '@/lib/normalize';
import { readLocationCookie } from '@/lib/location';
import ProfileHomeSkeleton from '@/components/skeletons/ProfileHome';
import ImageEditorModal from '@/components/profile/ImageEditorModal';
import LocationModal from '@/components/common/LocationModal';
import UnifiedFeed from '@/components/profile/UnifiedFeed';
import LikeShareReport from '@/components/common/LikeShareReport';
// Engagement-sprint dashboard cards (merged into Profile Home 2026-05-28 —
// replaced the simpler ViewersStatCard / FollowersStatCard duo with the full
// dashboard set so /profile = one unified page).
import ViewersCard from '@/components/profile/dashboard/cards/ViewersCard';
import FollowersCard from '@/components/profile/dashboard/cards/FollowersCard';
// Re-mounted 2026-08-27. This card was only ever imported by
// ProfileDashboardLayout, which lost its last render site in the 2026-05-28
// Profile Home merge above, so the creator-bio link engine has been dark since
// then: no creator could find their own shareable /u/{slug} URL from the
// dashboard. That is the site's one scalable off-page authority lever and it
// rendered nowhere while DR sat at 4.2.
import PromoteProfileCard from '@/components/profile/dashboard/cards/PromoteProfileCard';
import ProfileCompletenessCard from '@/components/profile/dashboard/cards/ProfileCompletenessCard';
import BadgesCard from '@/components/profile/dashboard/cards/BadgesCard';
import StreaksCard from '@/components/profile/dashboard/StreaksCard';
import TierCard from '@/components/profile/dashboard/TierCard';
import LeaderboardRail from '@/components/profile/dashboard/LeaderboardRail';
import RecentActivityFeed from '@/components/profile/dashboard/RecentActivityFeed';

function formatHeightInches(inches?: number | string | null): string {
  if (!inches && inches !== 0) return '-';
  const n = Number(inches);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const ft = Math.floor(n / 12);
  const inch = n % 12;
  return `${ft}' ${inch}\" (${n} in)`;
}

function mapBodyType(val?: string | number | null): string {
  if (val == null) return '-';
  const key = String(val).trim();
  const map: Record<string, string> = {
    '1': 'Very Skinny',
    '2': 'Skinny',
    '3': 'Average',
    '4': 'Muscular',
    '5': 'Overweight',
  };
  return map[key] || key || '-';
}

// Build link to public detail page for this user

type VerifyStatus = { status: 'pending'|'approved'|'denied'|'unverified'; reason?: string | null };
// GET /api/profile/usage applies Take(take) before it counts, and clamps take
// to 500 (ProfileController.GetOutboundUsage). So a count is exact below this
// and shown as "500+" at it.
const USAGE_TAKE = 500;

function formatPhone(raw?: unknown): string {
  const s = String(raw ?? '').replace(/[^0-9+]/g, '');
  if (!s) return '—';
  const m = s.match(/^\+?1?\s*(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return String(raw ?? '—') || '—';
}

// Staggered mount reveal for the dashboard cards. Uses `animate` on mount
// (NOT whileInView) so it is never scroll/observer-gated — a card can never
// ship blank if the observer doesn't fire. Reduced-motion renders final state.
const cardStagger = { show: { transition: { staggerChildren: 0.06 } } };
const cardItemVariant = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

export default function ProfileDetails() {
  const { data: session, status: authStatus } = useSession();
  const prefersReduced = useReducedMotion();
  const router = useRouter();
  const pathname = usePathname();
  const [detail, setDetail] = useState<any | null>(null);
  const [selfProfile, setSelfProfile] = useState<any | null>(null);
  const [fallbackVideos, setFallbackVideos] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [verify, setVerify] = useState<VerifyStatus | null>(null);
  const [usage, setUsage] = useState<{ chat: number; direct: number } | null>(null);
  // Image editor state (declare before any early returns)
  const [avatarEdit, setAvatarEdit] = useState<{ open: boolean; file: File | null }>({ open: false, file: null });
  const [bannerEdit, setBannerEdit] = useState<{ open: boolean; file: File | null }>({ open: false, file: null });
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationText, setLocationText] = useState<string>('');

  const [refreshKey, setRefreshKey] = useState(0);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [avatarProgress, setAvatarProgress] = useState(0);
  const { toast } = useToast();
  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    (async () => {
      try {
        // Load profile and verification status first for fast paint
        const [resp, vs] = await Promise.all([
          apiGet<any>(`/api/profile`, session),
          apiGet<any>(`/api/profile/verification/status`, session).then((s: any) => ({ status: (s?.status || s?.Status || '').toString().toLowerCase() as any })).catch(() => null as any),
        ]);
        if (vs) setVerify(vs);
        // Use normalizeProfile to handle casing
        const meProf = normalizeProfile(resp);
        setSelfProfile(meProf || null);
        // No plan name, price, billing date or limit is read here (FE11). The
        // /mo chips are counts only; the one proven limit is named on
        // /profile/plans behind the "Monthly limits" link.
        // Fetch monthly usage in background for /mo chips
        try {
          const u = await apiGet<any>(`/api/profile/usage?take=${USAGE_TAKE}`, session);
          const c = u?.counts || {};
          setUsage({ chat: Number(c.chat || 0), direct: Number(c.direct || 0) });
        } catch {}

        // Defer person detail fetch to idle time to keep UI responsive
        const schedule = (fn: () => void) => {
          try {
            const ric = (typeof window !== 'undefined' ? (window as any).requestIdleCallback : undefined) as
              | ((cb: any) => number)
              | undefined;
            if (ric) { ric(fn); } else { setTimeout(fn, 0); }
          } catch { setTimeout(fn, 0); }
        };
        const personId = meProf?.id;
        if (!personId) return;
        schedule(async () => {
          try {
            const d = await apiGet<any>(`/api/person/${personId}/detail`, session);
            setDetail(d);
            try {
              const vids = Array.isArray((d as any)?.videos) ? (d as any).videos : [];
              if (!vids || vids.length === 0) {
                const list = await apiGet<any[]>(`/api/video?personId=${personId}`, session);
                setFallbackVideos(Array.isArray(list) ? list : []);
              }
            } catch {}
          } catch (e: any) {
            setError(e?.message || 'Failed to load profile details');
          }
        });
      } catch (err: any) {
        if (err?.code === 'unauthorized' || err?.status === 401) {
          try {
            const cb = typeof window !== 'undefined' ? window.location.pathname : pathname || '/profile/home';
            router.push(`/login?returnTo=${encodeURIComponent(cb)}`);
            return;
          } catch {}
        }
        setError(err.message || 'Unknown error');
      }
    })();
  }, [authStatus, session, refreshKey]);

  // Global event to open Location modal
  useEffect(() => {
    function onOpen() { setLocationOpen(true); }
    function onUpdated() { try { setRefreshKey((k) => k + 1); } catch {} }
    try {
      window.addEventListener('app:openLocationModal', onOpen as any);
      window.addEventListener('profile:updated', onUpdated as any);
    } catch {}
    return () => {
      try {
        window.removeEventListener('app:openLocationModal', onOpen as any);
        window.removeEventListener('profile:updated', onUpdated as any);
      } catch {}
    };
  }, []);

  // Compute display location preferring site-wide location (cookie from Search modal),
  // falling back to profile's stateCode (no network fetching for speed).
  useEffect(() => {
    (async () => {
      // 1) Cookie-based location (set by search Location modal)
      const fromCookie = (() => { try { return readLocationCookie(); } catch { return null; } })();
      let sc = '' as string; let metroSlug = '' as string; let useProfileFallback = false;
      if (fromCookie && (fromCookie.state || fromCookie.metro)) {
        sc = String(fromCookie.state || '').toUpperCase();
        metroSlug = String(fromCookie.metro || '').toLowerCase();
      } else {
        const me: any = selfProfile || {};
        sc = String(me?.stateCode || '').toUpperCase();
        // No slug available from profile; we’ll display state-only via fallback
        metroSlug = '';
        useProfileFallback = true;
      }
      // Title-case the whole metro slug ("los-angeles" → "Los Angeles")
      if (!useProfileFallback && metroSlug) {
        const label = metroSlug
          .replace(/-market$/i, '')
          .split('-')
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        setLocationText(label);
      } else if (sc) {
        setLocationText(sc);
      } else {
        setLocationText('');
      }
    })();
  }, [selfProfile, session, refreshKey]);

  if (authStatus === 'loading') return <ProfileHomeSkeleton />;
  if (authStatus === 'unauthenticated') {
    try {
      const cb = typeof window !== 'undefined' ? window.location.pathname : pathname || '/profile/home';
      router.push(`/login?returnTo=${encodeURIComponent(cb)}`);
    } catch {}
    return null;
  }
  if (error) return <InlineBanner variant="error">{error}</InlineBanner>;

  // Allow rendering with selfProfile while detail is still loading
  const person: any = (detail && (detail as any).person) ? (detail as any).person : (selfProfile || {});
  const posts: any[] = (detail && Array.isArray((detail as any).posts)) ? (detail as any).posts : [];
  const videos: any[] = (detail && Array.isArray((detail as any).videos)) ? (detail as any).videos : [];
  const galleries: any[] = (detail && Array.isArray((detail as any).galleries)) ? (detail as any).galleries : [];
  function currentLocationLabel(p?: any, me?: any): string {
    const tryVals = [
      p?.locationName, p?.LocationName, p?.location, p?.Location, p?.cityName, p?.CityName,
      me?.locationName, me?.LocationName, me?.location, me?.Location, me?.cityName, me?.CityName,
    ];
    const first = tryVals.find((v) => typeof v === 'string' && String(v).trim().length > 0);
    if (first) return String(first);
    const state = (me?.stateCode || me?.StateCode || p?.stateCode || p?.StateCode || '').toString().toUpperCase();
    const metro = (me?.metroName || p?.metroName || '').toString();
    const metroCode = (me?.metroCode || p?.metroCode || '').toString();
    const parts: string[] = [];
    if (metro && metro.trim()) parts.push(metro.trim());
    if (state && state.trim()) parts.push(state.trim());
    if (!metro && metroCode && metroCode.trim()) parts.push(`#${metroCode.trim()}`);
    return parts.join(', ');
  }
  const locationLabel = currentLocationLabel(person, selfProfile);
  const videosEff = (Array.isArray(videos) && videos.length > 0) ? videos : fallbackVideos;
  const displayName: string = String(
    (person && ((person as any).displayName || (person as any).stageName || (person as any).name)) ||
    (selfProfile && ((selfProfile as any).displayName || (selfProfile as any).stageName || (selfProfile as any).name)) ||
    ''
  );
  const personId: number | undefined = (person as any)?.Id ?? (person as any)?.id ?? (selfProfile as any)?.Id ?? (selfProfile as any)?.id;
  const _stageNameForSlug = displayName || (person as any)?.stageName || (selfProfile as any)?.stageName || null;
  const _ownSlug = _stageNameForSlug ? (String(_stageNameForSlug).trim().toLowerCase().replace(/[\s_.]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '')) : null;
  const publicHref = (_ownSlug && _ownSlug.length >= 3) ? `/u/${_ownSlug}` : (personId ? `/details/${encodeURIComponent(String(personId))}` : '');

  async function uploadWithPresign(file: File, kind: 'avatar'|'banner', onProgress?: (pct: number)=>void): Promise<{ key: string; token?: string }> {
    // Use Next.js internal route (do not prefix API base)
    const res = await fetch('/api/profile/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'image/jpeg', kind }),
      cache: 'no-store',
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); msg = j?.detail || j?.message || msg; } catch {}
      throw new Error(msg);
    }
    const pres = await res.json();
    const putUrl = (pres as any).url ?? (pres as any).Url; const key = (pres as any).key ?? (pres as any).Key; const uploadToken = (pres as any).token ?? (pres as any).Token;
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest(); xhr.open('PUT', putUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
        xhr.upload.onprogress = (evt) => { if (evt.lengthComputable) onProgress?.(Math.round((evt.loaded/evt.total)*100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });
    } catch (e) {
      // Fallback: proxy the PUT server-side to bypass CORS in dev
      const resp = await fetch(`/api/profile/proxy-put?url=${encodeURIComponent(putUrl)}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
        cache: 'no-store',
      })
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`; try { const j = await resp.json(); msg = j?.detail || j?.message || msg } catch {}
        throw new Error(msg)
      }
    }
    return { key: key as string, token: uploadToken };
  }

  // Use a single presign to upload avatar triplet (base, _avatar, _thumb)
  async function uploadAvatarTriplet(blobs: { main: Blob; small?: Blob; thumb?: Blob }, onProgress?: (pct: number)=>void): Promise<{ base: {key:string, token?: string}, avatar: {key:string, token?:string}, thumb?: {key:string, token?:string} }> {
    const res = await fetch('/api/profile/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: 'avatar.jpg', contentType: 'image/jpeg', kind: 'avatar' }),
      cache: 'no-store',
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`; try { const j = await res.json(); msg = j?.detail || j?.message || msg } catch {}
      throw new Error(msg);
    }
    const pres = await res.json();
    const base = pres.base || pres.Base; const avatar = pres.avatar || pres.Avatar; const thumb = pres.thumb || pres.Thumb;
    if (!base?.url || !avatar?.url) throw new Error('Invalid presign response');
    // Upload base
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest(); xhr.open('PUT', base.url, true);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');
      xhr.upload.onprogress = (evt) => { if (evt.lengthComputable) onProgress?.(Math.round((evt.loaded/evt.total)*100)); };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(blobs.main);
    });
    // Upload avatar small if provided
    if (blobs.small) {
      await fetch(avatar.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs.small });
    }
    // Upload thumb if provided and presigned
    if (blobs.thumb && thumb?.url) {
      await fetch(thumb.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs.thumb });
    }
    return { base: { key: base.key, token: base.token }, avatar: { key: avatar.key, token: avatar.token }, thumb: thumb?.key ? { key: thumb.key, token: thumb.token } : undefined } as any;
  }

  async function saveAvatar(blobs: { main: Blob; small?: Blob; thumb?: Blob }) {
    setSavingAvatar(true);
    try {
      // Get userId from session (JWT subject) which is the authoritative source
      const idForUpdate = session?.user?.id || '';
      if (!idForUpdate) throw new Error('Missing user id from session');
      const mainFile = new File([blobs.main], 'avatar.jpg', { type: 'image/jpeg' });
      const avatarSmall = blobs.small ? new File([blobs.small], 'avatar_avatar.jpg', { type: 'image/jpeg' }) : null;
      const avatarThumb = blobs.thumb ? new File([blobs.thumb], 'avatar_thumb.jpg', { type: 'image/jpeg' }) : null;
      const trip = await uploadAvatarTriplet({ main: blobs.main, small: blobs.small, thumb: blobs.thumb }, setAvatarProgress);
      await apiPost(`/api/profile/avatar`, session, { key: trip.avatar.key, token: trip.avatar.token });
      setSelfProfile((prev: any) => ({ ...(prev||{}), profileImageLocation: trip.avatar.key, Id: idForUpdate }));
      try { window.dispatchEvent(new CustomEvent('profile:updated')); } catch {}
      toast({ title: 'Avatar updated', variant: 'success' });
      track('profile_avatar_updated', { ok: true });
    } catch (e: any) {
      const msg = e?.message || 'Failed to update avatar';
      toast({ title: 'Avatar update failed', description: msg, variant: 'destructive' });
      track('profile_avatar_updated', { ok: false, error: msg });
    } finally {
      setSavingAvatar(false);
      setAvatarEdit({ open: false, file: null });
    }
  }

  async function saveBanner(blobs: { main: Blob; thumb?: Blob }) {
    setSavingBanner(true);
    try {
      // Get userId from session (JWT subject) which is the authoritative source
      const idForUpdate = session?.user?.id || '';
      if (!idForUpdate) throw new Error('Missing user id from session');
      // Request banner pair presign and upload base + thumb
      const res = await fetch('/api/profile/presign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileName: 'banner.jpg', contentType: 'image/jpeg', kind: 'banner' }), cache: 'no-store' });
      if (!res.ok) { let m = `HTTP ${res.status}`; try { const j = await res.json(); m = j?.detail || j?.message || m } catch {}; throw new Error(m); }
      const pres = await res.json(); const base = pres.base || pres.Base; const t = pres.thumb || pres.Thumb;
      if (!base?.url) throw new Error('Invalid banner presign');
      await fetch(base.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs.main });
      if (blobs.thumb && t?.url) { try { await fetch(t.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blobs.thumb }); } catch {} }
      await apiPost(`/api/profile/banner`, session, { key: base.key, token: base.token });
      setSelfProfile((prev: any) => ({ ...(prev||{}), bannerImageLocation: base.key, Id: idForUpdate }));
      try { window.dispatchEvent(new CustomEvent('profile:updated')); } catch {}
      toast({ title: 'Banner updated', variant: 'success' });
      track('profile_banner_updated', { ok: true });
    } catch (e: any) {
      const msg = e?.message || 'Failed to update banner';
      toast({ title: 'Banner update failed', description: msg, variant: 'destructive' });
      track('profile_banner_updated', { ok: false, error: msg });
    } finally {
      setSavingBanner(false);
      setBannerEdit({ open: false, file: null });
    }
  }

  const verifyPill = (() => {
    const s = verify?.status || 'unverified';
    const map: Record<string, { label: string; cls: string; title: string; Icon: React.ComponentType<{ className?: string; strokeWidth?: number }> }> = {
      // Tokenized + contrast-safe: gold pending carries DARK grape text (white-on-gold fails AA);
      // unverified is a translucent nudge that reads on the banner scrim rather than a dead gray.
      approved:   { label: 'Verified',   cls: 'bg-success text-white',                                          title: 'Your identity is verified.',            Icon: BadgeCheck },
      pending:    { label: 'Pending',    cls: 'bg-[hsl(42_74%_62%)] text-[#2a1830]',                            title: 'Verification in review.',               Icon: Clock },
      denied:     { label: 'Denied',     cls: 'bg-destructive text-white',                                      title: 'Verification denied. Tap to review.',   Icon: ShieldAlert },
      unverified: { label: 'Unverified', cls: 'bg-white/15 text-white border border-white/40 backdrop-blur-sm', title: 'Tap to start verification.',            Icon: ShieldQuestion },
    };
    const v = map[s] || map.unverified;
    const I = v.Icon;
    return (
      <Link href="/profile/verifyaccount" title={v.title} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm transition-transform hover:scale-[1.04] ${v.cls}`}>
        <I className="h-3.5 w-3.5" strokeWidth={2} />
        {v.label}
      </Link>
    );
  })();

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* Overview card with quick info and edit action */}
        <div className="grid grid-cols-1 gap-6">
          <Card className="rounded-2xl border-border bg-card overflow-hidden profile-hero-glow">
            {/* Banner hero moved inside card */}
            {(() => {
              const p: any = person || {};
              const me: any = selfProfile || {};
              // Prefer freshly-updated selfProfile values first so UI reflects changes immediately
              const bannerPath =
                me.bannerImageLocation || me.bannerLocation || me.bannerUrl || me.banner ||
                p.bannerImageLocation || p.bannerLocation || p.bannerUrl || p.banner;
              // Use direct URL from profile, no helper transforms
              const bannerSrc = (typeof bannerPath === 'string' && bannerPath.trim()) ? String(bannerPath) : '/img/galaxy.svg';
              return (
                <div className="relative w-full h-48 md:h-56">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={bannerSrc}
                    alt="Profile banner"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      // Do not route through /media/resolve for absolute URLs; just show a safe default
                      (e.currentTarget as HTMLImageElement).src = '/img/galaxy.svg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
                  {/* Edit banner near banner */}
                  <button
                    type="button"
                    onClick={() => document.getElementById('pickBannerHome')?.click()}
                    className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-md bg-black/40 text-white px-2.5 py-1.5 text-xs hover:bg-black/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                    aria-label="Edit banner"
                    title="Edit banner"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Edit banner</span>
                  </button>
                  <div className="absolute left-4 bottom-3 flex items-end gap-4">
                    {(() => {
                      const p: any = person || {};
                      const me: any = selfProfile || {};
                      // Prefer freshly-updated selfProfile values first
                      const avatarPath =
                        me.profileImageLocation || me.profileImageUrl || me.avatarUrl || me.avatarLocation || me.profileImage ||
                        p.profileImageLocation || p.profileImageUrl || p.avatarUrl || p.avatarLocation || p.profileImage;
                      // Use direct URL from profile, no helper transforms
                      const avatarSrc = (typeof avatarPath === 'string' && avatarPath.trim()) ? String(avatarPath) : '/img/default-avatar.png';
                      return (
                        <div className="relative">
                          <div className="ribbon-ring shadow-lg">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={avatarSrc}
                              alt={(displayName || 'User') + ' avatar'}
                              className="block h-20 w-20 md:h-24 md:w-24 rounded-full border-2 border-card object-cover"
                              onError={(e) => {
                                // Do not route through /media/resolve for absolute URLs; just show a safe default
                                (e.currentTarget as HTMLImageElement).src = '/img/default-avatar.png';
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => document.getElementById('pickAvatarHome')?.click()}
                            className="absolute -right-2 -bottom-2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/70 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                            aria-label="Edit avatar"
                            title="Edit avatar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })()}
                    {displayName && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="font-serif text-white text-3xl md:text-4xl font-semibold tracking-tight drop-shadow-md">{displayName}</h1>
                        {verifyPill}
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-black/45 text-white border border-white/30"
                          title="This is your profile"
                        >
                          This is your profile
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <CardContent className="pt-4">
              {/* Quick access: View public profile & Share */}
              <div className="flex items-center justify-between gap-3">
                {publicHref ? (
                  <Link href={publicHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                    View public profile
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </Link>
                ) : (
                  <span className="text-sm text-muted-foreground">Your profile</span>
                )}
                {personId ? (
                  <LikeShareReport
                    type="profile"
                    id={personId}
                    share={true}
                    likeable={false}
                    report={false}
                  />
                ) : null}
              </div>
              {/* Hidden pickers */}
              <input id="pickAvatarHome" type="file" accept="image/*" className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) setAvatarEdit({ open: true, file: f }); (e.target as HTMLInputElement).value=''; }} />
              <input id="pickBannerHome" type="file" accept="image/*" className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) setBannerEdit({ open: true, file: f }); (e.target as HTMLInputElement).value=''; }} />
            </CardContent>
          </Card>
        </div>

        {/* Next-step nudge — sibling section below the hero */}
        <UploadNudgeBanner
          verifyStatus={verify?.status}
          galleryCount={galleries.length}
          videoCount={videosEff.length}
        />

        {/* Grouped sections (Body + Contact/Social merged) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {/* Body / Vitals */}
                <section className="relative rounded-2xl border border-border bg-card p-4">
                  <h2 className="font-serif text-base font-semibold mb-3">Your details</h2>
                  <Link href="/profile/editprofile" className="absolute right-4 top-4 text-xs font-medium text-primary hover:underline">Edit details</Link>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const raw = (person as any)?.height ?? (selfProfile as any)?.height ?? (selfProfile as any)?.Height;
                      const n = Number(raw);
                      if (!Number.isFinite(n) || n <= 0) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Height: {formatHeightInches(n)}</span>);
                    })()}
                    {(() => {
                      const raw = (person as any)?.weight ?? (selfProfile as any)?.weight ?? (selfProfile as any)?.Weight;
                      const n = Number(raw);
                      if (!Number.isFinite(n) || n <= 0) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Weight: {n} lbs</span>);
                    })()}
                    {(() => {
                      const raw = (person as any)?.bodyType
                              ?? (person as any)?.bodytype
                              ?? (selfProfile as any)?.bodyType
                              ?? (selfProfile as any)?.bodytype;
                      const label = mapBodyType(raw);
                      if (!label || label === '-') return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Body: {label}</span>);
                    })()}
                    {(() => {
                      const v = (person as any)?.sex ?? (selfProfile as any)?.sex ?? (selfProfile as any)?.Sex;
                      if (!v) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Sex: {v}</span>);
                    })()}
                    {(() => {
                      const v = (person as any)?.ethnicity ?? (selfProfile as any)?.ethnicity ?? (selfProfile as any)?.Ethnicity;
                      if (!v) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Ethnicity: {v}</span>);
                    })()}
                    {(() => {
                      const v = (person as any)?.eyes ?? (selfProfile as any)?.eyes ?? (selfProfile as any)?.Eyes;
                      if (!v) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Eyes: {v}</span>);
                    })()}
                    {(() => {
                      const v = (person as any)?.hair ?? (selfProfile as any)?.hair ?? (selfProfile as any)?.Hair;
                      if (!v) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Hair: {v}</span>);
                    })()}
                    {(() => {
                      const n = (person as any)?.nudity;
                      let label: string | undefined;
                      if (n != null) {
                        const nv = Number(n);
                        label = Number.isFinite(nv) ? (nv === 1 ? 'Yes' : nv === 0 ? 'No' : String(n)) : String(n);
                      } else {
                        const nl = (selfProfile as any)?.nudityLevel ?? (selfProfile as any)?.NudityLevel;
                        label = nl ? String(nl) : undefined;
                      }
                      if (!label) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Nudity: {label}</span>);
                    })()}
                    {(() => {
                      const v = (person as any)?.alterations ?? (selfProfile as any)?.alterations ?? (selfProfile as any)?.Alterations;
                      if (!v) return null;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Alterations: {v}</span>);
                    })()}
                    {(() => {
                      const s = String((person as any)?.sex || '').toLowerCase();
                      const showF = s.startsWith('f') || s === 'female' || s === 'woman' || s === 'girl';
                      if (!showF) return null;
                      const v = (person as any)?.boobs;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Boobs: {v || '—'}</span>);
                    })()}
                    {(() => {
                      const s = String((person as any)?.sex || '').toLowerCase();
                      const showM = s.startsWith('m') || s === 'male' || s === 'man' || s === 'boy';
                      if (!showM) return null;
                      const v = (person as any)?.dick;
                      return (<span className="px-2 py-1 rounded-full bg-muted text-foreground/85">Dick: {v ? `${v}\"` : '—'}</span>);
                    })()}
                  </div>
                  {person.moreInfo && (
                    <p className="mt-3 text-sm text-muted-foreground leading-6">{person.moreInfo}</p>
                  )}
                </section>

                {/* Contact & Social (merged) */}
                <section className="rounded-2xl border border-border bg-card p-4">
                  <h2 className="font-serif text-base font-semibold mb-1">Contact &amp; social</h2>
                  <p className="text-xs text-muted-foreground mb-3">These links are public. Keep deals, contact, and bookings on-platform.</p>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center gap-2.5">
                      <span className="ring-chip inline-grid h-6 w-6 place-items-center rounded-md"><MapPin className="h-3.5 w-3.5" /></span>
                      <span className="text-foreground">{locationText || '—'}</span>
                      <button
                        type="button"
                        className="ml-1 text-xs font-medium text-primary hover:underline"
                        onClick={() => setLocationOpen(true)}
                      >
                        Change
                      </button>
                    </div>
                    <div className="flex items-center gap-2.5 text-muted-foreground">
                      <span className="ring-chip inline-grid h-6 w-6 place-items-center rounded-md"><Mail className="h-3.5 w-3.5" /></span>
                      <span>{person.email || 'Add email'}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-muted-foreground">
                      <span className="ring-chip inline-grid h-6 w-6 place-items-center rounded-md"><Phone className="h-3.5 w-3.5" /></span>
                      <span>{formatPhone(person.phone)}</span>
                    </div>
                    {person.twitter && (<div className="text-muted-foreground text-xs pl-[2.125rem]">Twitter: {person.twitter}</div>)}
                    {person.snapchat && (<div className="text-muted-foreground text-xs pl-[2.125rem]">Snapchat: {person.snapchat}</div>)}
                    {person.url && (
                      <div className="flex items-center gap-2.5 text-muted-foreground">
                        <span className="ring-chip inline-grid h-6 w-6 place-items-center rounded-md"><Globe className="h-3.5 w-3.5" /></span>
                        <span className="truncate">{person.url}</span>
                      </div>
                    )}
                  </div>
                </section>
              </div>
              {/* Engagement dashboard — merged from the dashboard page 2026-05-28.
                  Cards are self-contained (each fetches its own data); placing
                  them here makes /profile the single landing for both profile
                  info AND engagement stats. */}
              <motion.section
                className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
                initial={prefersReduced ? false : 'hidden'}
                animate="show"
                variants={cardStagger}
              >
                <motion.div className="h-full" variants={cardItemVariant}><PromoteProfileCard /></motion.div>
                <motion.div className="h-full" variants={cardItemVariant}><ViewersCard /></motion.div>
                <motion.div className="h-full" variants={cardItemVariant}><FollowersCard /></motion.div>
                <motion.div className="h-full" variants={cardItemVariant}><ProfileCompletenessCard /></motion.div>
                <motion.div className="h-full" variants={cardItemVariant}><BadgesCard /></motion.div>
              </motion.section>
              {/* Plain grid (no motion wrappers): StreaksCard returns null for
                  0-day streaks, and an empty motion.div would occupy a grid cell
                  and push TierCard into the second column. */}
              <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <StreaksCard />
                <TierCard />
              </section>
              <LeaderboardRail />
              <RecentActivityFeed />

              {/* Content counts as navigation buttons */}
              <div className="grid grid-cols-3 gap-3">
                <Link href="/profile/posts" className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 hover:border-primary/40 hover:shadow-sm transition-all">
                  <span className="ring-chip inline-grid h-9 w-9 place-items-center rounded-lg"><Heart className="h-4 w-4" strokeWidth={1.75} /></span>
                  <div>
                    <div className="text-xs text-muted-foreground">Posts</div>
                    <div className="font-serif text-lg font-semibold tabular-nums">{posts.length}</div>
                  </div>
                </Link>
                <Link href="/profile/galleries" className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 hover:border-primary/40 hover:shadow-sm transition-all">
                  <span className="ring-chip inline-grid h-9 w-9 place-items-center rounded-lg"><ImageIcon className="h-4 w-4" strokeWidth={1.75} /></span>
                  <div>
                    <div className="text-xs text-muted-foreground">Galleries</div>
                    <div className="font-serif text-lg font-semibold tabular-nums">{galleries.length}</div>
                  </div>
                </Link>
                <Link href="/profile/videos" className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 hover:border-primary/40 hover:shadow-sm transition-all">
                  <span className="ring-chip inline-grid h-9 w-9 place-items-center rounded-lg"><Video className="h-4 w-4" strokeWidth={1.75} /></span>
                  <div>
                    <div className="text-xs text-muted-foreground">Videos</div>
                    <div className="font-serif text-lg font-semibold tabular-nums">{videosEff.length}</div>
                  </div>
                </Link>
              </div>

              {/* Usage limits summary */}
              {(() => {
                const pct = (cur: number, tot: number) => (tot > 0 ? cur / tot : 0);
                const chip = (label: string, cur?: number | null, tot?: number | null, saturateAt?: number) => {
                  const u = (cur != null && Number.isFinite(Number(cur))) ? Number(cur) : undefined;
                  const t = (tot != null && Number.isFinite(Number(tot))) ? Number(tot) : undefined;
                  const r = (u != null && t != null && t > 0) ? pct(u, t) : 0;
                  const tone = (u != null && t != null && t > 0)
                    ? (r < 0.6 ? 'bg-success/10 text-foreground border-success/30' : (r < 0.9 ? 'bg-[hsl(42_74%_62%)]/25 text-foreground border-[hsl(42_74%_50%)]/45' : 'bg-destructive/10 text-foreground border-destructive/30'))
                    : 'bg-muted text-foreground border-border';
                  const text = (u != null && t != null && t > 0)
                    ? `${u}/${t}`
                    : (t != null && t > 0)
                      ? `${t}`
                      : (u != null)
                        ? (saturateAt != null && u >= saturateAt ? `${saturateAt}+` : `${u}`)
                        : '—';
                  return (
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${tone}`}>
                      <span className="font-medium">{label}</span>
                      <span className="tabular-nums">{text}</span>
                    </span>
                  );
                };
                // The plan pill, the billing date shown as a reset date and the
                // plan management link are gone (FE11). The limits link below
                // goes to the one-line notice at /profile/plans. The /mo chips
                // take no denominator: the contact form cap counts mail logged
                // to the member (not sends), and a limit beside a count is only
                // true when both are; the notice page names the chat limit.
                return (
                  <div className="mt-2 rounded-2xl border border-border bg-card p-3 text-xs text-muted-foreground" data-testid="usage-summary">
                    <div className="flex flex-wrap gap-2 items-center">
                      {/* Counts only, no denominators. PostLimit, GalleryLimit and
                          VideoLimit reach the client but nothing in the API reads
                          them: PlanUsageService enforces chat and messages alone.
                          A denominator here reddened toward a cap that does not
                          exist and talked creators out of publishing. */}
                      {chip('Posts', posts.length)}
                      {chip('Galleries', galleries.length)}
                      {chip('Videos', videosEff.length)}
                      {chip('Msgs/mo', usage?.direct ?? undefined, undefined, USAGE_TAKE)}
                      {chip('Chats/mo', usage?.chat ?? undefined, undefined, USAGE_TAKE)}
                      <Link href="/profile/plans" className="ml-auto underline">Monthly limits</Link>
                    </div>
                  </div>
                );
              })()}

              {/* Full details (legacy layout hidden) */}
              <div className="hidden grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><strong>Stage Name:</strong> {person.stageName || '—'}</div>
                <div><strong>Height:</strong> {formatHeightInches(person.height ?? selfProfile?.height ?? (selfProfile as any)?.Height)}</div>
                <div><strong>Weight:</strong> {(() => { const w = person?.weight ?? selfProfile?.weight ?? (selfProfile as any)?.Weight; return (w ?? '') !== '' && w != null ? `${w} lbs` : '-'; })()}</div>
                <div><strong>Body Type:</strong> {(() => { const raw = person.bodyType ?? person.bodytype ?? (selfProfile as any)?.bodyType ?? (selfProfile as any)?.bodytype; const label = mapBodyType(raw); return label || '-'; })()}</div>
                <div><strong>Sex:</strong> {person.sex || (selfProfile as any)?.sex || (selfProfile as any)?.Sex || '-'}</div>
                <div><strong>Ethnicity:</strong> {person.ethnicity || (selfProfile as any)?.ethnicity || (selfProfile as any)?.Ethnicity || '-'}</div>
                <div><strong>Eyes:</strong> {person.eyes || (selfProfile as any)?.eyes || (selfProfile as any)?.Eyes || '-'}</div>
                <div><strong>Hair:</strong> {person.hair || (selfProfile as any)?.hair || (selfProfile as any)?.Hair || '-'}</div>
                <div><strong>Nudity Level:</strong> {person.nudity || '-'}</div>
                <div><strong>Alterations:</strong> {person.alterations || (selfProfile as any)?.alterations || (selfProfile as any)?.Alterations || '-'}</div>
                {(() => { const s = String(person.sex || '').toLowerCase(); const show = s.startsWith('f') || s === 'female' || s === 'woman' || s === 'girl' || !s; return show ? (<div><strong>Boobs Size:</strong> {person.boobs || '—'}</div>) : null; })()}
                {(() => { const s = String(person.sex || '').toLowerCase(); const show = s.startsWith('m') || s === 'male' || s === 'man' || s === 'boy' || !s; return show ? (<div><strong>Dick Size:</strong> {person.dick ? `${person.dick}"` : '—'}</div>) : null; })()}
                <div className="sm:col-span-2 md:col-span-3"><strong>More Info / Bio:</strong> {person.moreInfo || '—'}</div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span><strong>Phone:</strong> {formatPhone(person.phone)}</span></div>
                <div><strong>Twitter:</strong> {person.twitter || '—'}</div>
                <div><strong>Snapchat:</strong> {person.snapchat || '—'}</div>
                <div className="sm:col-span-2 md:col-span-3 flex items-center gap-2"><Globe className="h-4 w-4 text-muted-foreground" /><span><strong>Website:</strong> {person.url || '—'}</span></div>
                <div><strong>Relocate:</strong> {person.travel ? 'Yes' : 'No'}</div>
              </div>
        {/* Your published content — distinct from the "Recent activity" card
            above, which shows activity ON you (likes, views). */}
        {personId ? (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-serif text-lg font-semibold">Your content</h2>
              <span className="juicy-rule ml-1" aria-hidden="true" />
            </div>
            <UnifiedFeed personId={personId} take={12} />
          </section>
        ) : null}
      </div>
      {avatarEdit.open && avatarEdit.file && (
        <ImageEditorModal
          kind="avatar"
          file={avatarEdit.file}
          onCancel={() => setAvatarEdit({ open: false, file: null })}
          // Generate main + small + thumb variants for avatar
          output={{ main: { w: 1080, h: 1080, q: 0.9 }, small: { w: 80, h: 80, q: 0.8 }, thumb: { w: 400, h: 647, q: 0.85 } }}
          onExport={(imgs) => { saveAvatar({ main: imgs.main, small: imgs.small, thumb: imgs.thumb }); }}
          saving={savingAvatar}
        />
      )}
      {bannerEdit.open && bannerEdit.file && (
        <ImageEditorModal
          kind="banner"
          file={bannerEdit.file}
          onCancel={() => setBannerEdit({ open: false, file: null })}
          onExport={(imgs) => { saveBanner({ main: imgs.main, thumb: imgs.thumb }); }}
          saving={savingBanner}
        />
      )}

      {/* Change Location Modal */}
      <LocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onChanged={() => {
          setLocationOpen(false);
          setRefreshKey((k) => k + 1);
          try { window.dispatchEvent(new CustomEvent('profile:updated')); } catch {}
        }}
      />
    </div>
  );
}

// W2.1/W2.3 — Next-step nudge for the post-login dashboard. Three states:
//   - unverified/denied → push to start verification
//   - pending → reassurance + link to status
//   - approved + no content → push to upload first gallery/video
// Hides once the user is approved AND has at least one gallery and one video.
function UploadNudgeBanner({ verifyStatus, galleryCount, videoCount }: {
  verifyStatus?: 'pending' | 'approved' | 'denied' | 'unverified' | null;
  galleryCount: number;
  videoCount: number;
}) {
  if (verifyStatus == null) return null;

  const isApproved = verifyStatus === 'approved';
  const isPending = verifyStatus === 'pending';
  const hasGallery = galleryCount > 0;
  const hasVideo = videoCount > 0;

  if (isApproved && hasGallery && hasVideo) return null;

  let chip = '';
  let title = '';
  let body = '';
  const ctas: { label: string; href: string; primary?: boolean }[] = [];

  if (!isApproved && !isPending) {
    chip = 'Step 1 of 2';
    title = 'Verify your ID to start posting';
    // Every claim here is code-checked, because the line it replaced was not:
    // it promised verification "makes your profile visible in search", and
    // search3 returns unverified profiles perfectly well. What is true:
    //   - Search3Repository.cs:209 sorts on `vrf.Val DESC` FIRST, so verified
    //     profiles rank above unverified ones on every search.
    //   - GalleryController.cs:963 and :1363 hold EXPLICIT content from an
    //     unverified uploader. Non-explicit content publishes either way, so
    //     do not say verification unlocks galleries and videos outright.
    //   - It carries the verified badge, and 25 of the 100 completeness points
    //     (ProfileController.cs:381; a separate 20 of 72 in the /top ranking).
    //   - Verified plus opted in is also what shows a creator by name instead
    //     of blurred and anonymous on the leaderboards.
    body = 'One ID check lets your explicit photos and videos go live, ranks your profile above unverified ones in search, and adds your verified badge. Takes about 5 minutes; we approve within 24 hours.';
    ctas.push({ label: 'Verify your ID', href: '/profile/verifyaccount', primary: true });
  } else if (isPending) {
    chip = 'Under review';
    title = 'Verification under review';
    body = 'Most reviews finish within 24 hours. As soon as you are approved, you can publish galleries and videos.';
    ctas.push({ label: 'View status', href: '/profile/verifyaccount' });
  } else if (!hasGallery && !hasVideo) {
    chip = 'Last step';
    title = 'Upload your first gallery and video';
    body = 'Profiles with content show up in search and on the home feed. Galleries and videos are how producers find you.';
    ctas.push({ label: 'Upload gallery', href: '/profile/galleries/create', primary: true });
    ctas.push({ label: 'Upload video', href: '/profile/videos/create' });
  } else if (!hasGallery) {
    chip = 'Almost there';
    title = 'Add a gallery to round out your profile';
    body = 'Galleries help producers and fans browse your work alongside your videos.';
    ctas.push({ label: 'Upload gallery', href: '/profile/galleries/create', primary: true });
  } else {
    chip = 'Almost there';
    title = 'Add a video to your profile';
    body = 'Videos drive the most engagement. Even a short introduction works.';
    ctas.push({ label: 'Upload video', href: '/profile/videos/create', primary: true });
  }

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-primary/40 bg-card shadow-sm">
      <div className="btn-ribbon h-1.5 w-full" aria-hidden="true" />
      <div className="p-5">
        <span className="ring-chip inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold mb-2">{chip}</span>
        <h2 className="juicy-title text-xl font-semibold mb-1.5">{title}</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-prose">{body}</p>
        <div className="flex flex-wrap items-center gap-2.5">
          {ctas.map((c) => (
            c.primary ? (
              <Link
                key={c.href}
                href={c.href}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold btn-ribbon text-white shadow-sm transition-transform hover:scale-[1.02]"
              >
                {c.label}
              </Link>
            ) : (
              <Button key={c.href} asChild variant="outline" size="default">
                <Link href={c.href}>{c.label}</Link>
              </Button>
            )
          ))}
        </div>
      </div>
    </div>
  );
}
